<template>
  <v-container fill-height fluid grid-list-xl>
    <v-layout wrap>

      <v-flex md6 sm12 lg6 first-row>
        <material-card>
          <v-toolbar slot="header" color="primary" class="elevation-4">
            <v-toolbar-title>User Detail</v-toolbar-title>
            <v-spacer></v-spacer>
            <v-btn
              class="ma-2"
              outlined
              color="white"
              v-if="loggedUser.role === '1' && !userDetails.canBeEdited"
              @click.stop="enableEditing()"
            >Edit User</v-btn>
            <v-btn
              class="ma-2"
              color="pink"
              v-if="userDetails.canBeEdited"
              @click.stop="updateUser(userDetails.data)"
            >Save Changes</v-btn>
            <v-btn
              class="ma-2"
              outlined
              color="white"
              v-if="userDetails.canBeEdited"
              @click.stop="cancelEditing()"
            >Cancel</v-btn>
          </v-toolbar>
          <v-form ref="userDetailsForm">
            <v-form-base
              v-bind:schema="userSchema"
              v-bind:value="userDetails.data"
              :key="dataLoaded"
            />
            <v-switch
      v-model="userDetails.data.is_active"
      inset
      :label="`User is active: ${userDetails.data.is_active.toString()}`"
      :disabled="userDetails.canBeEdited === false"
    ></v-switch>
          </v-form>
        </material-card>
      </v-flex>

      <v-flex md6 sm12 lg6 first-row>
        <material-card>
          <v-toolbar slot="header" color="primary" class="elevation-4">
            <v-toolbar-title>Reset Password</v-toolbar-title>
            <v-spacer></v-spacer>
          </v-toolbar>
          <v-form ref="userPasswordForm">
          <v-form-base
              v-bind:value="changePasswordForm"
              v-bind:schema="changePasswordSchema"
              :key="dataLoaded"
            />
          </v-form>
           <v-btn
              class="ma-2"
              color="primary"
              dark
              v-if="loggedUser.role === '1' && !userDetails.canBeEdited"
              @click.stop="resetPassword()"
            >Submit</v-btn>
        </material-card>
      </v-flex>
    </v-layout>
  </v-container>
</template>

<script>
import apiService from '../services'
import { mapState } from 'vuex'
import VFormBase from 'vuetify-form-base'
import { tablePaginationMixin } from '@/mixins/tablePaginationMixin'
import { statusColor } from '@/mixins/statusColor'

// Form rules
const formRules = {
  required: field => v => (v != null && !!v && v.length > 0) || `${field} is required`,
  requiredSelect: field => v => v || `${field} is required`,
  validEmail: v => /.+@.+\..+/.test(v) || 'E-mail must be valid'
}

export default {
  mixins: [
    tablePaginationMixin,
    statusColor
  ],

  components: {
    VFormBase
  },

  data () {
    return {
      dataLoaded: false, // Used to trigger form components re-rendering
      changePasswordForm: {
        password: '',
        password_repeat: ''
      },
      userDetails: {
        canBeEdited: false,
        data: {}
      },
      // Campaign rules
      hasRules: false,
      loading: true
    }
  },

  computed: {
    ...mapState({
      auth: 'auth',
      alerts: (state) => state.alert.snackbars
    }),
    loggedIn () {
      return this.auth.loggedIn
    },
    loggedUser () {
      return this.auth.user || null
    },

    // Get general entity statuses from store
    ...mapState('init', ['statuses', 'interventionAreas']),

    // VformBase schema for campaign form
    userSchema () {
      return {
        first_name: {
          type: 'text',
          label: 'First Name',
          flex: 6,
          readonly: this.userDetails.canBeEdited === false
        },
        last_name: {
          type: 'text',
          label: 'Last Name',
          flex: 6,
          readonly: this.userDetails.canBeEdited === false
        },
        username: {
          type: 'text',
          label: 'Email/Username',
          flex: 12,
          readonly: this.userDetails.canBeEdited === false
        }
      }
    },
    //  change password form
    changePasswordSchema () {
      return {
        password: {
          type: 'password',
          label: 'Password',
          clearable: true,
          flex: 6,
          rules: [formRules.required('Password')]
        },
        password_repeat: {
          type: 'password',
          label: 'Confirm Password',
          clearable: true,
          flex: 6,
          rules: [formRules.required('Password'),
            (value) => value === this.changePasswordForm.password || 'The password confirmation does not match.']
        }
      }
    }
  },

  mounted () {
    this.getUser(this.$route.params.userId)
  },

  methods: {
    getUser (userId) {
      this.loading = true

      apiService
        .getUser(userId)
        .then((response) => {
          setTimeout(() => {
            this.userDetails.data = response
            this.dataLoaded = !this.dataLoaded
          }, 800)
        })
        .catch((error) => {
          this.loading = false
          this.$store.dispatch('alert/showSnackbar', {
            color: 'error',
            text: 'Errore nel caricamento dei dati'
          })
          console.log(error)
        })
    },

    resetPassword () {
      this.changePasswordForm.user_id = this.$route.params.userId
      if (this.$refs.userPasswordForm.validate()) {
        apiService
          .resetPassword(this.changePasswordForm)
          .then(response => {
            console.log(response)
            this.$store.dispatch('alert/showSnackbar', {
              color: 'success',
              type: 'success',
              text: 'Password aggiornata con successo'
            })
          })
          .catch(error => {
            console.log(error)
            this.$store.dispatch('alert/showSnackbar', {
              color: 'error',
              text: 'Impossibile aggiornare la password'
            })
          })
      }
    },

    /* #####################################################
    #                Campaign details edit                 #
    ###################################################### */

    //  Enable editing of Campaign details
    enableEditing () {
      // Change control variable value
      this.userDetails.canBeEdited = !this.userDetails.canBeEdited
      // Save backup data
      this.userDetails.backup = this.userDetails.data
    },

    cancelEditing () {
      // Change control variable value
      this.userDetails.canBeEdited = !this.userDetails.canBeEdited
      // Restore backup data
      this.userDetails.data = this.userDetails.backup
      // Clear backup data
      this.userDetails.backup = {}
    },

    updateUser (params) {
      // Update Campaign based on campaign_id
      const data = params

      data.contact = {
        first_name: data.first_name,
        last_name: data.last_name,
        email: data.username
      }
      apiService
        .updateUser(this.$route.params.userId, data)
        .then((response) => {
          this.$store.dispatch('alert/showSnackbar', {
            color: 'success',
            text: 'User succesfully updated'
          })
          // Close edit menu
          this.userDetails.canBeEdited = !this.userDetails.canBeEdited
        })
        .catch(() => {
          this.loading = false
          this.$store.dispatch('alert/showSnackbar', {
            color: 'error',
            text: 'Error updating User'
          })
          // Close edit menu
          this.userDetails.canBeEdited = !this.userDetails.canBeEdited
        })
    }
  }
}
</script>
